import { QUEENS_CELL_COUNT, QUEENS_SIZE, type QueenCell } from './types';

export function queenIndices(board: readonly QueenCell[]): number[] {
  return board.flatMap((value, index) => (value === 2 ? [index] : []));
}

export function findQueensConflicts(
  board: readonly QueenCell[],
  regions: readonly number[],
): Set<number> {
  const queens = queenIndices(board);
  const conflicts = new Set<number>();
  for (let first = 0; first < queens.length; first += 1) {
    for (let second = first + 1; second < queens.length; second += 1) {
      const a = queens[first];
      const b = queens[second];
      const rowA = Math.floor(a / QUEENS_SIZE);
      const rowB = Math.floor(b / QUEENS_SIZE);
      const columnA = a % QUEENS_SIZE;
      const columnB = b % QUEENS_SIZE;
      const adjacentDiagonal = Math.abs(rowA - rowB) === 1 && Math.abs(columnA - columnB) === 1;
      if (rowA === rowB || columnA === columnB || regions[a] === regions[b] || adjacentDiagonal) {
        conflicts.add(a);
        conflicts.add(b);
      }
    }
  }
  return conflicts;
}

export function isQueensComplete(board: readonly QueenCell[], regions: readonly number[]): boolean {
  return (
    queenIndices(board).length === QUEENS_SIZE && findQueensConflicts(board, regions).size === 0
  );
}

export function countQueensSolutions(regions: readonly number[], limit = 2): number {
  if (regions.length !== QUEENS_CELL_COUNT) return 0;
  let count = 0;
  const usedColumns = new Set<number>();
  const usedRegions = new Set<number>();
  const columns: number[] = [];

  function search(row: number) {
    if (count >= limit) return;
    if (row === QUEENS_SIZE) {
      count += 1;
      return;
    }
    for (let column = 0; column < QUEENS_SIZE; column += 1) {
      const region = regions[row * QUEENS_SIZE + column];
      const previousColumn = columns[row - 1];
      if (
        usedColumns.has(column) ||
        usedRegions.has(region) ||
        (previousColumn !== undefined && Math.abs(previousColumn - column) === 1)
      ) {
        continue;
      }
      columns.push(column);
      usedColumns.add(column);
      usedRegions.add(region);
      search(row + 1);
      usedColumns.delete(column);
      usedRegions.delete(region);
      columns.pop();
      if (count >= limit) return;
    }
  }

  search(0);
  return count;
}

export function solveQueens(regions: readonly number[]): number[] | null {
  let solution: number[] | null = null;
  const usedColumns = new Set<number>();
  const usedRegions = new Set<number>();
  const indices: number[] = [];

  function search(row: number): boolean {
    if (row === QUEENS_SIZE) {
      solution = [...indices];
      return true;
    }
    for (let column = 0; column < QUEENS_SIZE; column += 1) {
      const index = row * QUEENS_SIZE + column;
      const region = regions[index];
      const previous = indices.at(-1);
      const previousColumn = previous === undefined ? undefined : previous % QUEENS_SIZE;
      if (
        usedColumns.has(column) ||
        usedRegions.has(region) ||
        (previousColumn !== undefined && Math.abs(previousColumn - column) === 1)
      ) {
        continue;
      }
      indices.push(index);
      usedColumns.add(column);
      usedRegions.add(region);
      if (search(row + 1)) return true;
      usedColumns.delete(column);
      usedRegions.delete(region);
      indices.pop();
    }
    return false;
  }

  search(0);
  return solution;
}

export function isRegionConnected(regions: readonly number[], region: number): boolean {
  const members = regions.flatMap((value, index) => (value === region ? [index] : []));
  if (members.length === 0) return false;
  const remaining = new Set(members);
  const pending = [members[0]];
  remaining.delete(members[0]);
  while (pending.length > 0) {
    const index = pending.pop()!;
    for (const neighbor of orthogonalNeighbors(index)) {
      if (remaining.has(neighbor)) {
        remaining.delete(neighbor);
        pending.push(neighbor);
      }
    }
  }
  return remaining.size === 0;
}

export function orthogonalNeighbors(index: number): number[] {
  const row = Math.floor(index / QUEENS_SIZE);
  const column = index % QUEENS_SIZE;
  const neighbors: number[] = [];
  if (row > 0) neighbors.push(index - QUEENS_SIZE);
  if (row < QUEENS_SIZE - 1) neighbors.push(index + QUEENS_SIZE);
  if (column > 0) neighbors.push(index - 1);
  if (column < QUEENS_SIZE - 1) neighbors.push(index + 1);
  return neighbors;
}
